import { useEffect, useMemo, useState } from 'react';

import type {
  SkillMappingRequest,
  SkillMappingResponse,
  SkillMappingResult,
} from '@revelio/data-access';
import { calculateStringSimilarity } from '@revelio/iso-utility';

import { useCustomSkillSelectionList } from '../../../shared/selection-lists/hooks/useCustomSkillSelectionList';
import { normalizeForMatch } from '../utils/normalizeForMatch';

const SKILL_MAPPER_URL = '/api/ai-search/mapper/skill';

const SCORE_THRESHOLD = 0.75;

export interface ResolvedSkill {
  name: string;
  entityId: number;
  levelId: number;
  taxonomyName: string;
}

export interface UseMapUserSkillsResult {
  resolvedSkills: ResolvedSkill[];
  unresolvedSkills: string[];
  isLoading: boolean;
}

type SkillLevelKey = 'skill_0' | 'skill_1' | 'skill_2' | 'skill_3' | 'skill_4';
type ApiFieldKey =
  | 'skill_k150'
  | 'skill_k1500'
  | 'skill_k5000'
  | 'skill_k15000';

interface GranularityLevel {
  apiField: ApiFieldKey;
  selectionListKey: SkillLevelKey;
  levelId: number;
}

/** Ordered from finest to coarsest granularity */
const GRANULARITY_LEVELS: GranularityLevel[] = [
  { apiField: 'skill_k15000', selectionListKey: 'skill_3', levelId: 4 },
  { apiField: 'skill_k5000', selectionListKey: 'skill_2', levelId: 3 },
  { apiField: 'skill_k1500', selectionListKey: 'skill_1', levelId: 2 },
  { apiField: 'skill_k150', selectionListKey: 'skill_0', levelId: 1 },
];

interface SkillRef {
  id: number;
  short_name: string;
  long_name: string;
}

type TaxonomyData = Record<SkillLevelKey, { refs: SkillRef[] } | undefined>;

const findExactMatchInRefs = (
  refs: SkillRef[],
  targetName: string
): SkillRef | undefined => {
  const normalized = normalizeForMatch(targetName);
  return refs.find(
    (ref) =>
      normalizeForMatch(ref.short_name) === normalized ||
      normalizeForMatch(ref.long_name) === normalized
  );
};

const resolveSkillFromApiResult = (
  skillName: string,
  apiResult: SkillMappingResult,
  taxonomyData: TaxonomyData
): ResolvedSkill | null => {
  if (apiResult.score !== undefined && apiResult.score < SCORE_THRESHOLD) {
    return null;
  }

  // Calculate similarity at each level, pick the best match
  const levelsWithSimilarity = GRANULARITY_LEVELS.map((level) => {
    const apiValue = apiResult[level.apiField];
    if (!apiValue) return null;

    const similarity = calculateStringSimilarity(
      skillName.toLowerCase().trim(),
      apiValue.toLowerCase().trim()
    );

    return { ...level, apiValue, similarity };
  })
    .filter((v): v is NonNullable<typeof v> => v !== null)
    .sort((a, b) => b.similarity - a.similarity);

  // Build lookup order: best similarity first, then priority order
  const lookupOrder: Array<{
    apiValue: string;
    selectionListKey: SkillLevelKey;
    levelId: number;
  }> = [];

  if (levelsWithSimilarity.length > 0) {
    const bestMatch = levelsWithSimilarity[0];
    if (bestMatch) {
      lookupOrder.push(bestMatch);
      const used = new Set([bestMatch.selectionListKey]);
      for (const level of GRANULARITY_LEVELS) {
        if (!used.has(level.selectionListKey)) {
          const apiValue = apiResult[level.apiField];
          if (apiValue) {
            lookupOrder.push({
              apiValue,
              selectionListKey: level.selectionListKey,
              levelId: level.levelId,
            });
            used.add(level.selectionListKey);
          }
        }
      }
    }
  }

  for (const level of lookupOrder) {
    const skillLevel = taxonomyData[level.selectionListKey];
    if (!skillLevel?.refs) continue;

    const match = findExactMatchInRefs(skillLevel.refs, level.apiValue);
    if (match) {
      return {
        name: skillName,
        entityId: match.id,
        levelId: level.levelId,
        taxonomyName: match.long_name || match.short_name,
      };
    }
  }

  return null;
};

const fuzzyMatchInTaxonomy = (
  skillName: string,
  taxonomyData: TaxonomyData
): ResolvedSkill | null => {
  const normalized = normalizeForMatch(skillName);
  if (!normalized) return null;

  // Try each level from finest to coarsest
  for (const level of GRANULARITY_LEVELS) {
    const skillLevel = taxonomyData[level.selectionListKey];
    if (!skillLevel?.refs) continue;

    // Exact normalized match
    const exactMatch = skillLevel.refs.find(
      (ref) =>
        normalizeForMatch(ref.short_name) === normalized ||
        normalizeForMatch(ref.long_name) === normalized
    );

    if (exactMatch) {
      return {
        name: skillName,
        entityId: exactMatch.id,
        levelId: level.levelId,
        taxonomyName: exactMatch.long_name || exactMatch.short_name,
      };
    }

    // Fuzzy: check containment
    const containsMatch = skillLevel.refs.find((ref) => {
      const shortNorm = normalizeForMatch(ref.short_name);
      const longNorm = normalizeForMatch(ref.long_name);
      return (
        shortNorm.includes(normalized) ||
        normalized.includes(shortNorm) ||
        longNorm.includes(normalized) ||
        normalized.includes(longNorm)
      );
    });

    if (containsMatch) {
      const similarity = Math.max(
        calculateStringSimilarity(skillName, containsMatch.short_name),
        calculateStringSimilarity(skillName, containsMatch.long_name)
      );
      if (similarity >= 0.4) {
        return {
          name: skillName,
          entityId: containsMatch.id,
          levelId: level.levelId,
          taxonomyName: containsMatch.long_name || containsMatch.short_name,
        };
      }
    }
  }

  return null;
};

export const useMapUserSkills = (
  userSkills: string[]
): UseMapUserSkillsResult => {
  const [apiResults, setApiResults] = useState<{
    skills: string[];
    response: SkillMappingResponse;
  } | null>(null);
  const [apiLoading, setApiLoading] = useState(false);

  const [{ data: taxonomyData, fetching: taxonomyFetching }] =
    useCustomSkillSelectionList(0);

  const skillsKey = useMemo(
    () => Array.from(new Set(userSkills)).join(','),
    [userSkills]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const uniqueSkills = Array.from(new Set(userSkills));
    if (uniqueSkills.length === 0) {
      setApiResults({ skills: [], response: { results: [] } });
      return;
    }

    let cancelled = false;
    setApiLoading(true);

    const body: SkillMappingRequest = {
      skills: uniqueSkills,
      additional_fields: [
        'score',
        'skill_k150',
        'skill_k1500',
        'skill_k5000',
        'skill_k15000',
      ],
    };

    fetch(SKILL_MAPPER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        if (cancelled) return;
        if (!response.ok) {
          console.error(`Skill mapper request failed (${response.status})`);
          setApiResults({ skills: uniqueSkills, response: { results: [] } });
        } else {
          const data = (await response.json()) as SkillMappingResponse;
          setApiResults({ skills: uniqueSkills, response: data });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Skill mapper API error:', error);
        setApiResults({ skills: uniqueSkills, response: { results: [] } });
      })
      .finally(() => {
        if (!cancelled) setApiLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [skillsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const result = useMemo((): Omit<UseMapUserSkillsResult, 'isLoading'> => {
    if (!taxonomyData?.selectionList || !apiResults) {
      return { resolvedSkills: [], unresolvedSkills: [] };
    }

    const selectionList = taxonomyData.selectionList;
    const taxonomy: TaxonomyData = {
      skill_0: selectionList.skill_0 as TaxonomyData['skill_0'],
      skill_1: selectionList.skill_1 as TaxonomyData['skill_1'],
      skill_2: selectionList.skill_2 as TaxonomyData['skill_2'],
      skill_3: selectionList.skill_3 as TaxonomyData['skill_3'],
      skill_4: selectionList.skill_4 as TaxonomyData['skill_4'],
    };

    const { skills: apiSkillsOrder, response } = apiResults;
    const uniqueSkills = Array.from(new Set(userSkills));
    const resolved: ResolvedSkill[] = [];
    const unresolved: string[] = [];

    for (const skillName of uniqueSkills) {
      if (!skillName) continue;

      // Look up API result by matching the skill order used for the API call
      const apiIndex = apiSkillsOrder.indexOf(skillName);
      const apiResult =
        apiIndex >= 0 ? response.results?.[apiIndex] : undefined;

      if (apiResult) {
        const mapped = resolveSkillFromApiResult(
          skillName,
          apiResult,
          taxonomy
        );
        if (mapped) {
          resolved.push(mapped);
          continue;
        }
      }

      // Fallback to fuzzy matching
      const fuzzyMatch = fuzzyMatchInTaxonomy(skillName, taxonomy);
      if (fuzzyMatch) {
        resolved.push(fuzzyMatch);
      } else {
        unresolved.push(skillName);
      }
    }

    return { resolvedSkills: resolved, unresolvedSkills: unresolved };
  }, [taxonomyData, apiResults, userSkills]);

  return {
    ...result,
    isLoading:
      taxonomyFetching || apiLoading || (!apiResults && userSkills.length > 0),
  };
};
